// Cave84 — Wine Analysis Page
// Visual: faithfully follows /mockups/cave84_wine_analysis.html
// Data:   spec §4.2 F2 + §6.3 Screen 3 / shared-types WineAnalysis
//
// Layout: back-bar → hero → tabs → two-column grid (main + 380px sidebar)
// Analysis sections: English LTR layout (migrated from Hebrew RTL).

import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type { Bottle, Cellar, CriticScore, Location, Vintage, WineStyle } from '@cave84/shared-types';
import type { QualityLabel, Appellation } from '@cave84/shared-types';
import { useWine } from '@/hooks/useWines';
import { useProducer } from '@/hooks/useProducers';
import { useAppellation } from '@/hooks/useAppellations';
import { useVintagesByWine } from '@/hooks/useVintages';
import { useWineAnalysis, useQueueWineAnalysis, useSaveOwnerOverride, useClearOwnerOverrides } from '@/hooks/useWineAnalysis';
import { useCriticScoresByVintage, useAddCriticScore, useDeleteCriticScore } from '@/hooks/useCriticScores';
import { useBottlesByVintage, useBottlesByVintageIds } from '@/hooks/useBottles';
import { useCellars } from '@/hooks/useCellars';
import { useCellarLocations } from '@/hooks/useLocations';
import { useAuthStore } from '@/stores/auth.store';
import { CRITIC_DISPLAY, formatScore } from '@/services/criticScores';
import {
  CONDITION_LABELS, FILL_LABELS, FORMAT_LABELS, SOURCE_LABELS, STATUS_LABELS,
  isConcerningCondition, isConcerningFill,
} from '@/services/bottleLabels';
import { useVintageChartEntry } from '@/hooks/useVintageCharts';
import { usePriceHistory } from '@/hooks/useMarketPrices';
import { WishlistButton } from '@/components/WishlistButton';
import { EditWineModal } from '@/components/EditWineModal';
import { Money } from '@/components/Money';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const STYLE_LABELS: Record<WineStyle, string> = {
  red: 'Red', white: 'White', rose: 'Rosé', sparkling: 'Sparkling',
  fortified: 'Fortified', sweet: 'Sweet', orange: 'Orange',
};

type DrinkingStatus = 'too_young' | 'developing' | 'peak' | 'declining' | 'over_the_hill';

function computeDrinkingStatus(v: Vintage): DrinkingStatus {
  const now = new Date().getFullYear();
  const w = v.drinking_window_current;
  if (!w) return 'too_young';
  if (now < w.from_year) return 'too_young';
  if (now < w.peak_from) return 'developing';
  if (now <= w.peak_to) return 'peak';
  if (now <= w.to_year) return 'declining';
  return 'over_the_hill';
}

const DRINKING_LABEL: Record<DrinkingStatus, string> = {
  too_young: 'Too Young', developing: 'Developing', peak: 'Peak',
  declining: 'Declining', over_the_hill: 'Past Peak',
};

const DRINKING_TEXT: Record<DrinkingStatus, string> = {
  too_young:    'text-[#7E9A98]',
  developing:   'text-amber-300',
  peak:         'text-gold',
  declining:    'text-orange-300',
  over_the_hill:'text-zinc-400',
};

const STATUS_BADGE: Record<string, { label: string; color: string }> = {
  pending:         { label: 'Pending generation', color: 'bg-amber-900/30 text-amber-400 border-amber-800' },
  generated:       { label: 'AI generated',       color: 'bg-emerald-900/30 text-emerald-400 border-emerald-800' },
  needs_review:    { label: 'Needs review',        color: 'bg-orange-900/30 text-orange-400 border-orange-800' },
  outdated:        { label: 'Outdated',            color: 'bg-zinc-800 text-zinc-400 border-zinc-700' },
  manually_edited: { label: 'Owner edited',        color: 'bg-gold/10 text-gold border-gold/30' },
};

function inferVintageChartRegion(
  appellation: Appellation | undefined,
  wineStyle: string,
  cuveeName: string | null | undefined
): string | undefined {
  if (!appellation) return undefined;
  const region    = (appellation.region    ?? '').toLowerCase();
  const subregion = (appellation.subregion ?? '').toLowerCase();
  const name      = (cuveeName            ?? '').toLowerCase();
  if (region.includes('burgundy') || region.includes('bourgogne'))
    return wineStyle === 'white' ? 'Burgundy_White' : 'Burgundy_Red';
  if (region.includes('sauternes') || subregion.includes('sauternes') || subregion.includes('barsac'))
    return 'Sauternes';
  if (region.includes('bordeaux')) {
    if (subregion.includes('saint-emilion') || subregion.includes('pomerol') || subregion.includes('fronsac'))
      return 'Bordeaux_Right';
    return 'Bordeaux_Left';
  }
  if (region.includes('champagne')) return 'Champagne';
  if (region.includes('rhone') || region.includes('rhône')) {
    if (subregion.includes('hermitage') || subregion.includes('cornas') || subregion.includes('crozes'))
      return 'Rhone_North';
    return 'Rhone_South';
  }
  if (region.includes('piedmont') || region.includes('piemonte')) {
    if (name.includes('barolo') || subregion.includes('barolo')) return 'Barolo';
    return undefined;
  }
  if (region.includes('tuscany') || region.includes('toscana')) {
    if (name.includes('brunello') || subregion.includes('brunello')) return 'Brunello';
    if (subregion.includes('bolgheri')) return 'Bolgheri';
    return undefined;
  }
  if (region.includes('veneto') || name.includes('amarone') || subregion.includes('valpolicella'))
    return 'Amarone';
  if (region.includes('loire')) return 'Loire';
  if (region.includes('alsace')) return 'Alsace';
  if (region.includes('mosel') || region.includes('rheingau')) return 'Mosel_Rheingau';
  // The six regions added to the chart set in July 2026 had no mapping here,
  // so their bottles never linked to a chart even once seeded.
  if (region.includes('rioja')) return 'Rioja';
  if (region.includes('ribera')) return 'Ribera_del_Duero';
  if (region.includes('priorat')) return 'Priorat';
  if (region.includes('tokaj')) return 'Tokaji';
  if (region.includes('nahe')) return 'Nahe';
  return undefined;
}

const QUALITY_BADGE_MAP: Record<QualityLabel, { label: string; cls: string }> = {
  exceptional:  { label: 'Exceptional',  cls: 'bg-emerald-900/60 text-emerald-300 border-emerald-700' },
  excellent:    { label: 'Excellent',    cls: 'bg-green-900/60 text-green-300 border-green-700' },
  very_good:    { label: 'Very Good',    cls: 'bg-lime-900/60 text-lime-300 border-lime-700' },
  good:         { label: 'Good',         cls: 'bg-yellow-900/60 text-yellow-300 border-yellow-700' },
  average:      { label: 'Average',      cls: 'bg-orange-900/60 text-orange-300 border-orange-700' },
  poor:         { label: 'Poor',         cls: 'bg-red-900/60 text-red-400 border-red-700' },
  not_declared: { label: 'Not Declared', cls: 'bg-zinc-800/60 text-zinc-400 border-zinc-600' },
};

function tsToDate(ts: unknown): Date | undefined {
  if (!ts) return undefined;
  try {
    return typeof (ts as { toDate?: () => Date }).toDate === 'function'
      ? (ts as { toDate: () => Date }).toDate()
      : new Date((ts as { seconds: number }).seconds * 1000);
  } catch { return undefined; }
}
function formatAsOf(ts: unknown): string | undefined {
  const d = tsToDate(ts);
  return d ? d.toLocaleDateString('en', { month: 'short', year: 'numeric' }) : undefined;
}
function formatFullDate(ts: unknown): string | undefined {
  const d = tsToDate(ts);
  return d ? d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : undefined;
}
function isStale(nextRefreshDue: unknown): boolean {
  const d = tsToDate(nextRefreshDue);
  return d ? d < new Date() : false;
}

// ---------------------------------------------------------------------------
// VintageChartCrossRef
// ---------------------------------------------------------------------------

function VintageChartCrossRef({ region, year }: { region: string; year: number }) {
  const { data: entry, isLoading } = useVintageChartEntry(region, year);
  if (isLoading || !entry) return null;
  const badge = QUALITY_BADGE_MAP[entry.quality_label];
  return (
    <Link to="/vintage-charts"
      className="flex items-center gap-3 rounded border border-border-medium bg-surface-1 px-4 py-3 hover:border-gold/40 transition-colors">
      <div className="flex-shrink-0 text-center w-10">
        {entry.score !== null
          ? <p className="text-2xl font-bold text-text-primary tabular-nums" dir="ltr">{entry.score}</p>
          : <p className="text-sm text-text-muted">ND</p>}
        <p className="text-[11px] text-text-muted">/ 100</p>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-text-muted mb-0.5">Vintage chart — {year}</p>
        <div className="flex items-center gap-2 flex-wrap">
          {badge && <span className={`text-[11px] px-1.5 py-0.5 rounded border ${badge.cls}`}>{badge.label}</span>}
          <span className="text-xs text-text-secondary capitalize">{entry.drinking_status.replace(/_/g, ' ')}</span>
        </div>
      </div>
      <svg className="w-4 h-4 text-text-muted flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    </Link>
  );
}

// ---------------------------------------------------------------------------
// StalenessBanner
// ---------------------------------------------------------------------------

function StalenessBanner({ nextRefreshDue, onQueue, isQueuing, isOwner }:
  { nextRefreshDue: unknown; onQueue: () => void; isQueuing: boolean; isOwner: boolean }) {
  if (!isStale(nextRefreshDue)) return null;
  return (
    <div className="flex items-start gap-3 rounded border border-amber-800 bg-amber-900/20 px-4 py-3">
      <span className="text-amber-400 mt-0.5 flex-shrink-0">!</span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-amber-300">Analysis may be outdated</p>
        <p className="text-xs text-amber-500 mt-0.5">Critic scores may have changed.</p>
      </div>
      {isOwner && (
        <button type="button" onClick={onQueue} disabled={isQueuing}
          className="flex-shrink-0 px-3 py-1.5 rounded border border-amber-700 bg-amber-900/40 text-amber-300 text-xs font-medium hover:bg-amber-800/50 disabled:opacity-40 transition-colors">
          {isQueuing ? 'Queuing...' : 'Refresh'}
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// HeAnalysisSection — Hebrew RTL section
// ---------------------------------------------------------------------------

interface HeAnalysisSectionProps {
  sectionNum: string;
  title: string;
  content: string;
  isOwner?: boolean;
  onSave?: (text: string) => void;
  isSaving?: boolean;
}

function HeAnalysisSection({ sectionNum, title, content, isOwner, onSave, isSaving }: HeAnalysisSectionProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(content);
  const isLead = sectionNum === '01';

  return (
    <section className="mb-14 pb-14 border-b border-border-subtle last:border-b-0">
      <div>
        <h2 className="font-display text-[32px] font-medium text-text-primary leading-[1.15] tracking-[-0.01em]">
          <span className="font-display italic text-gold text-[13px] mr-3 font-normal align-middle">
            {sectionNum}
          </span>
          {title}
        </h2>
        <div className="w-16 h-px bg-gold mt-3.5 mb-6" />
        {editing ? (
          <div className="space-y-3">
            <textarea value={draft} onChange={e => setDraft(e.target.value)} rows={6}
              className="w-full px-3 py-2 rounded border border-border-medium bg-surface-2 text-text-primary text-[15px] resize-y focus:outline-none focus:border-gold" />
            <div className="flex justify-start gap-2">
              <button type="button" onClick={() => { onSave?.(draft); setEditing(false); }} disabled={isSaving}
                className="px-3 py-1.5 rounded bg-gold text-black text-xs font-semibold hover:bg-gold/90 disabled:opacity-40">
                {isSaving ? 'Saving...' : 'Save'}
              </button>
              <button type="button" onClick={() => setEditing(false)}
                className="px-3 py-1.5 rounded border border-border-medium bg-surface-2 text-text-secondary text-xs">
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <>
            {isLead
              ? <p className="text-[18px] leading-[1.8] text-text-primary font-medium border-l-2 border-gold pl-[18px] mb-6">{content}</p>
              : <p className="text-[16.5px] leading-[1.85] text-text-primary mb-4">{content}</p>
            }
            {isOwner && (
              <button type="button" onClick={() => { setDraft(content); setEditing(true); }}
                className="text-xs text-text-muted hover:text-gold transition-colors mt-2">
                Edit
              </button>
            )}
          </>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// SideCard shell
// ---------------------------------------------------------------------------

function SideCard({ title, subtitle, children }: {
  title: string; subtitle?: string; children: React.ReactNode;
}) {
  return (
    <div className="bg-surface-1 border border-border-subtle overflow-hidden">
      <div className="px-[18px] py-3.5 border-b border-border-subtle">
        <p className="text-[11px] text-text-tertiary uppercase tracking-[0.18em] font-semibold">{title}</p>
        {subtitle && (
          <p className="font-display text-[17px] text-text-primary font-medium mt-1 tracking-[-0.01em]">{subtitle}</p>
        )}
      </div>
      <div className="px-[18px] py-4">{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sidebar — Critic Scores
// ---------------------------------------------------------------------------

function SidebarCriticScores({ vintageId }: { vintageId: string | undefined }) {
  const { data: scores = [], isLoading } = useCriticScoresByVintage(vintageId);

  // Prefer explicitly flagged top-3; fall back to top 3 by normalised score value
  const flagged = scores.filter((s: CriticScore) => s.is_top_3);
  const top3: CriticScore[] = flagged.length >= 1
    ? flagged.slice(0, 3)
    : [...scores]
        .sort((a: CriticScore, b: CriticScore) => {
          const norm = (s: CriticScore) => s.score_type === '20_pt' ? s.score_value * 5 : s.score_value;
          return norm(b) - norm(a);
        })
        .slice(0, 3);

  if (!vintageId || isLoading || !top3.length) {
    return (
      <SideCard title="Top Critic Scores" subtitle="—">
        <p className="text-xs text-text-muted py-2">No scores recorded.</p>
      </SideCard>
    );
  }

  const avg = top3.reduce((acc: number, s: CriticScore) => {
    const val = s.score_type === '20_pt' ? s.score_value * 5 : s.score_value;
    return acc + val;
  }, 0) / top3.length;

  return (
    <SideCard title="Top Critic Scores" subtitle={top3.length === 1 ? '1 critic' : `${top3.length} critics`}>
      <div className="flex flex-col gap-0">
        {top3.map((score: CriticScore, i: number) => {
          const info = CRITIC_DISPLAY[score.critic] ?? { label: score.critic };
          return (
            <div key={score.id}
              className={`grid grid-cols-[1fr_auto] items-center gap-3 pb-3.5 ${i < top3.length - 1 ? 'border-b border-border-subtle mb-3.5' : ''}`}>
              <div>
                <p className="font-display text-[15px] text-text-primary font-medium leading-[1.2]">{info.label}</p>
                {score.critic_individual && (
                  <p className="text-[11px] text-text-tertiary mt-0.5 italic">{score.critic_individual}</p>
                )}
              </div>
              <div className="text-right">
                <span className="font-display text-[28px] text-gold font-medium leading-none tracking-[-0.02em]">
                  {formatScore(score)}
                </span>
                <span className="font-display text-[12px] text-text-muted italic">
                  /{score.score_type === '20_pt' ? '20' : '100'}
                </span>
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex justify-between items-baseline pt-3.5 border-t border-border-medium mt-1">
        <span className="text-[11px] text-text-tertiary uppercase tracking-[0.16em] font-semibold">Aggregate</span>
        <span className="font-display text-[22px] text-text-primary font-medium">{avg.toFixed(1)}</span>
      </div>
    </SideCard>
  );
}

// ---------------------------------------------------------------------------
// Sidebar — My Bottles
// ---------------------------------------------------------------------------

function SidebarBottles({ vintageId, totalCount }: { vintageId: string | undefined; totalCount: number }) {
  const { data: bottles = [], isLoading } = useBottlesByVintage(vintageId);
  const inCellar = bottles.filter((b: Bottle) => b.status === 'in_cellar');

  const byCellar = inCellar.reduce<Record<string, Bottle[]>>((acc, b: Bottle) => {
    const key = b.current_cellar_id;
    if (!acc[key]) acc[key] = [];
    (acc[key] as Bottle[]).push(b);
    return acc;
  }, {});

  return (
    <SideCard title="My Bottles" subtitle="Across all cellars">
      <div className="text-center pb-4 border-b border-border-subtle mb-3.5">
        <p className="font-display text-[48px] leading-none text-gold font-normal tracking-[-0.02em]">
          {isLoading ? '–' : inCellar.length || totalCount}
        </p>
        <p className="text-[11px] text-text-tertiary uppercase tracking-[0.16em] mt-1 font-semibold">bottles</p>
      </div>
      {!isLoading && Object.keys(byCellar).length > 0 && (
        <div className="flex flex-col gap-2.5">
          {Object.entries(byCellar).map(([cellarId, btls]) => (
            <div key={cellarId} className="grid grid-cols-[auto_1fr_auto] gap-2.5 items-center text-[12.5px]">
              <span className="font-mono text-text-secondary font-medium">x{btls.length}</span>
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-[0.08em] rounded-[2px] border text-cellar-cyprus border-cellar-cyprus/45 bg-cellar-cyprus/6">
                {cellarId}
              </span>
              <span className="text-[11px] text-text-tertiary text-right">
                {btls.length > 0 ? formatAsOf((btls as Bottle[])[0]!.purchase_date) ?? '' : ''}
              </span>
            </div>
          ))}
        </div>
      )}
      {!isLoading && inCellar.length === 0 && (
        <p className="text-xs text-text-muted text-center py-2">No bottles for this vintage.</p>
      )}
    </SideCard>
  );
}

// ---------------------------------------------------------------------------
// Sidebar — Drinking Window visual
// ---------------------------------------------------------------------------

function SidebarDrinkingWindow({ vintage }: { vintage: Vintage }) {
  const w = vintage.drinking_window_current;
  const now = new Date().getFullYear();
  const status = computeDrinkingStatus(vintage);

  if (!w) {
    return (
      <SideCard title="Drinking Window" subtitle="Analysis pending">
        <p className="text-sm text-text-muted">Drinking window data is not yet available. Run analysis to generate it.</p>
      </SideCard>
    );
  }

  const timeStart = vintage.year;
  const timeEnd   = w.to_year + 5;
  const total     = timeEnd - timeStart;

  function pct(year: number) {
    return Math.max(0, Math.min(100, ((year - timeStart) / total) * 100));
  }

  const STATUS_TEXT: Record<DrinkingStatus, string> = {
    too_young:    '"Hold."',
    developing:   '"Approaching."',
    peak:         '"Drink now."',
    declining:    '"Drink soon."',
    over_the_hill: '"Past peak."',
  };

  const yearsLeft = w.from_year - now;
  const STATUS_SUBTITLE: Record<DrinkingStatus, string> = {
    too_young:    yearsLeft > 0 ? `Wait ${yearsLeft} more year${yearsLeft !== 1 ? 's' : ''} minimum.` : 'Almost there.',
    developing:   'Drinking well, not yet at peak.',
    peak:         'In peak drinking window now.',
    declining:    `Drink within ${w.to_year - now} year${w.to_year - now !== 1 ? 's' : ''}.`,
    over_the_hill: 'Past optimal drinking window.',
  };

  return (
    <SideCard title="Drinking Window" subtitle={`Status: ${DRINKING_LABEL[status]}`}>
      <div className="flex justify-between mb-2 font-mono text-[11px] text-text-tertiary">
        <span dir="ltr">{vintage.year}</span>
        <span dir="ltr" className="text-text-primary font-semibold">{now}</span>
        <span dir="ltr">{w.to_year}</span>
      </div>
      <div className="relative h-2 bg-surface-3 rounded-[1px] overflow-visible border border-border-subtle">
        <div className="absolute h-full rounded-[1px]"
          style={{
            left: `${pct(w.from_year)}%`,
            width: `${pct(w.to_year) - pct(w.from_year)}%`,
            background: 'linear-gradient(90deg,#7E9A98 0%,#C49255 35%,#C49255 75%,#C49255 100%)',
          }} />
        <div className="absolute top-[-4px] bottom-[-4px] w-0.5 bg-white"
          style={{ left: `${pct(now)}%` }}>
          <span className="absolute bottom-[-20px] left-1/2 -translate-x-1/2 text-[11px] text-text-primary font-mono font-semibold tracking-[0.1em] whitespace-nowrap">
            NOW
          </span>
        </div>
      </div>
      <p className={`mt-8 text-center font-display text-[16px] italic ${DRINKING_TEXT[status]}`}>
        {STATUS_TEXT[status]}
      </p>
      <p className="mt-2 text-center text-[11px] text-text-tertiary leading-relaxed">
        {STATUS_SUBTITLE[status]}
      </p>
      <div className="mt-3 px-3 py-2 border border-gold/20 bg-gold/5 rounded-[2px] text-[11px] text-gold text-center font-mono">
        Peak window: {w.peak_from} to {w.peak_to}
      </div>
    </SideCard>
  );
}

// ---------------------------------------------------------------------------
// Sidebar — Market
// ---------------------------------------------------------------------------

function SidebarMarket({ vintageId }: { vintageId: string | undefined }) {
  const { data: history = [], isLoading } = usePriceHistory(vintageId);
  const latest = history[0];

  if (!vintageId || isLoading || !latest) {
    return (
      <SideCard title="Market" subtitle="Avg per bottle">
        <p className="text-xs text-text-muted py-2">No price data.</p>
      </SideCard>
    );
  }

  return (
    <SideCard title="Market" subtitle="Avg per bottle, current">
      <div className="flex justify-between items-baseline mb-3.5">
        <span className="font-display text-[32px] text-text-primary font-medium leading-none">
          <Money value={latest.price} currency="EUR" minWidthCh={7} forceVisible />
        </span>
      </div>
      <div className="flex flex-col gap-0 text-[12px] text-text-tertiary">
        <div className="flex justify-between py-1 border-b border-border-subtle">
          <span>Format</span>
          <span className="font-mono text-text-secondary">{latest.format}</span>
        </div>
        <div className="flex justify-between py-1 border-b border-border-subtle">
          <span>Confidence</span>
          <span className="font-mono text-text-secondary capitalize">{latest.confidence}</span>
        </div>
        <div className="flex justify-between py-1">
          <span>Last refresh</span>
          <span className="font-mono text-text-secondary">{formatAsOf(latest.date_observed) ?? '—'}</span>
        </div>
      </div>
    </SideCard>
  );
}

// ---------------------------------------------------------------------------
// Tab: Analysis
// ---------------------------------------------------------------------------

function AnalysisTab({ wineId, selectedVintage, isOwner, vintageChartRegion }:
  { wineId: string; selectedVintage: Vintage | null; isOwner: boolean; vintageChartRegion: string | undefined }) {
  const { data: analysis } = useWineAnalysis(wineId);
  const queueMutation  = useQueueWineAnalysis();
  const saveMutation   = useSaveOwnerOverride();
  const clearMutation  = useClearOwnerOverrides();
  const [confirmClear, setConfirmClear] = useState(false);

  if (!analysis) {
    return (
      <div className="py-16 text-center space-y-4">
        <p className="text-5xl">🍷</p>
        <p className="text-text-secondary text-sm">No analysis generated yet.</p>
        {isOwner && (
          <button type="button"
            onClick={() => queueMutation.mutate({ wineId, priority: 'high' })}
            disabled={queueMutation.isPending}
            className="px-5 py-2 rounded bg-gold text-black font-semibold text-sm hover:bg-gold/90 disabled:opacity-40">
            {queueMutation.isPending ? 'Queuing...' : '+ Queue Wine Analysis'}
          </button>
        )}
      </div>
    );
  }

  const statusInfo = STATUS_BADGE[analysis.status] ?? STATUS_BADGE['generated']!;

  return (
    <div>
      <div className="mb-6">
        <StalenessBanner nextRefreshDue={analysis.next_refresh_due}
          onQueue={() => queueMutation.mutate({ wineId, priority: 'high' })}
          isQueuing={queueMutation.isPending} isOwner={isOwner} />
      </div>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-8">
        <span className={`inline-flex items-center px-2 py-0.5 rounded border text-xs ${statusInfo.color}`}>
          {statusInfo.label}
        </span>
        {isOwner && (
          <div className="flex items-center gap-3">
            <button type="button"
              onClick={() => queueMutation.mutate({ wineId })}
              disabled={queueMutation.isPending || clearMutation.isPending}
              className="text-xs text-text-muted hover:text-gold transition-colors">
              {queueMutation.isPending ? 'Queuing...' : 'Regenerate'}
            </button>
            {analysis.manually_edited && !confirmClear && (
              <button type="button" onClick={() => setConfirmClear(true)}
                className="text-xs text-text-muted hover:text-red-400 transition-colors">
                From scratch
              </button>
            )}
            {confirmClear && (
              <div className="flex items-center gap-2 bg-red-900/20 border border-red-800 rounded px-2 py-1">
                <span className="text-xs text-red-300">Discard your edits?</span>
                <button type="button" disabled={clearMutation.isPending}
                  onClick={() => { clearMutation.mutate({ wineId }); setConfirmClear(false); }}
                  className="text-xs font-semibold text-red-400 disabled:opacity-40">
                  {clearMutation.isPending ? 'Clearing...' : 'Yes'}
                </button>
                <button type="button" onClick={() => setConfirmClear(false)}
                  className="text-xs text-text-muted">Cancel</button>
              </div>
            )}
          </div>
        )}
      </div>
      {analysis.producer_note && (
        <HeAnalysisSection sectionNum="01" title="Producer Profile"
          content={analysis.producer_note} isOwner={isOwner}
          onSave={text => saveMutation.mutate({ wineId, sectionKey: 'producer_note', content: text })}
          isSaving={saveMutation.isPending} />
      )}
      {analysis.terroir_note && (
        <HeAnalysisSection sectionNum="02" title="Terroir & Vintage"
          content={analysis.terroir_note} isOwner={isOwner}
          onSave={text => saveMutation.mutate({ wineId, sectionKey: 'terroir_note', content: text })}
          isSaving={saveMutation.isPending} />
      )}
      {vintageChartRegion && selectedVintage && (
        <section className="mb-14 pb-14 border-b border-border-subtle">
          <div className="mb-4">
            <h2 className="font-display text-[32px] font-medium text-text-primary leading-[1.15]">
              <span className="font-display italic text-gold text-[13px] mr-3 font-normal align-middle">03</span>
              Vintage Chart
            </h2>
            <div className="w-16 h-px bg-gold mt-3.5 mb-6" />
          </div>
          <VintageChartCrossRef region={vintageChartRegion} year={selectedVintage.year} />
        </section>
      )}
      {analysis.comparable_wines.length > 0 && (
        <section>
          <div className="mb-4">
            <h2 className="font-display text-[32px] font-medium text-text-primary leading-[1.15]">
              <span className="font-display italic text-gold text-[13px] mr-3 font-normal align-middle">04</span>
              Similar Wines
            </h2>
            <div className="w-16 h-px bg-gold mt-3.5 mb-6" />
          </div>
          <div className="border border-border-medium divide-y divide-border-subtle">
            {analysis.comparable_wines.map((w, i) => (
              <div key={i} className="px-4 py-3 bg-surface-1">
                <p className="text-sm font-medium text-text-primary">{w.producer} — {w.cuvee}</p>
                <p className="text-xs text-text-muted mt-0.5">{w.note}</p>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Critic Scores
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Add-score form — owner only
// ---------------------------------------------------------------------------

const CRITIC_OPTIONS = Object.entries(CRITIC_DISPLAY) as [string, { label: string; abbr: string }][];

interface AddScoreFormState {
  critic: string;
  critic_individual: string;
  score_type: '100_pt' | '20_pt' | '5_star';
  score_value: string;
  score_range: string;
  review_text: string;
  source_url: string;
}

const BLANK_FORM: AddScoreFormState = {
  critic: 'wine_advocate',
  critic_individual: '',
  score_type: '100_pt',
  score_value: '',
  score_range: '',
  review_text: '',
  source_url: '',
};

function AddScorePanel({ vintageId, onClose }: { vintageId: string; onClose: () => void }) {
  const [form, setForm] = useState<AddScoreFormState>(BLANK_FORM);
  const { mutate, isPending, isError } = useAddCriticScore();

  function set<K extends keyof AddScoreFormState>(key: K, val: AddScoreFormState[K]) {
    setForm(prev => ({ ...prev, [key]: val }));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const val = parseFloat(form.score_value);
    if (!Number.isFinite(val) || val <= 0) return;
    mutate(
      {
        vintageId,
        score: {
          vintage_id: vintageId,
          critic: form.critic as CriticScore['critic'],
          critic_individual: form.critic_individual,
          score_type: form.score_type,
          score_value: val,
          ...(form.score_range ? { score_range: form.score_range } : {}),
          ...(form.review_text ? { review_text: form.review_text } : {}),
          ...(form.source_url ? { source_url: form.source_url } : {}),
          is_top_3: true,
          source: 'manual_entry',
        },
      },
      { onSuccess: onClose }
    );
  }

  const inputCls = 'w-full bg-surface-2 border border-border-medium px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-gold';
  const labelCls = 'block text-[11px] font-semibold uppercase tracking-[0.1em] text-text-muted mb-1';

  return (
    <form onSubmit={handleSubmit} className="border border-gold/40 bg-surface-1 p-5 space-y-4 mt-4">
      <p className="font-display text-[15px] text-text-primary font-medium tracking-[-0.01em]">Add Critic Score</p>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <span className={labelCls}>Critic</span>
          <select value={form.critic} onChange={e => set('critic', e.target.value)}
            className={inputCls + ' cursor-pointer'}>
            {CRITIC_OPTIONS.map(([key, { label }]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
        </div>
        <div>
          <span className={labelCls}>Individual (optional)</span>
          <input type="text" value={form.critic_individual}
            onChange={e => set('critic_individual', e.target.value)}
            placeholder="e.g. Allen Meadows"
            className={inputCls} />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div>
          <span className={labelCls}>Scale</span>
          <select value={form.score_type} onChange={e => set('score_type', e.target.value as AddScoreFormState['score_type'])}
            className={inputCls + ' cursor-pointer'}>
            <option value="100_pt">100-pt</option>
            <option value="20_pt">20-pt</option>
            <option value="5_star">5-star</option>
          </select>
        </div>
        <div>
          <span className={labelCls}>Score</span>
          <input type="number" step="0.5" min="0" max="100"
            value={form.score_value}
            onChange={e => set('score_value', e.target.value)}
            placeholder={form.score_type === '20_pt' ? '18.5' : '97'}
            required
            className={inputCls} />
        </div>
        <div>
          <span className={labelCls}>Range (opt.)</span>
          <input type="text" value={form.score_range}
            onChange={e => set('score_range', e.target.value)}
            placeholder="97–99"
            className={inputCls} />
        </div>
      </div>

      <div>
        <span className={labelCls}>Review text (optional)</span>
        <textarea value={form.review_text} onChange={e => set('review_text', e.target.value)}
          rows={3} placeholder="Tasting note..."
          className={inputCls + ' resize-none'} />
      </div>

      <div>
        <span className={labelCls}>Source URL (optional)</span>
        <input type="url" value={form.source_url}
          onChange={e => set('source_url', e.target.value)}
          placeholder="https://..."
          className={inputCls} />
      </div>

      {isError && <p className="text-xs text-red-400">Save failed — try again.</p>}

      <div className="flex gap-3 pt-1">
        <button type="submit" disabled={isPending}
          className="px-5 py-2 bg-gold text-black text-[12px] font-semibold uppercase tracking-[0.1em] hover:bg-gold/90 disabled:opacity-50 transition-colors">
          {isPending ? 'Saving…' : 'Save Score'}
        </button>
        <button type="button" onClick={onClose}
          className="px-5 py-2 border border-border-medium text-[12px] font-semibold uppercase tracking-[0.1em] text-text-muted hover:text-text-primary transition-colors">
          Cancel
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Tab: Critic Scores
// ---------------------------------------------------------------------------

function CriticScoresTab({ vintageId, isOwner }: { vintageId: string | undefined; isOwner: boolean }) {
  const { data: scores = [], isLoading } = useCriticScoresByVintage(vintageId);
  const [showForm, setShowForm] = useState(false);
  const { mutate: deleteScore, isPending: isDeleting } = useDeleteCriticScore();
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  if (!vintageId) return <p className="text-text-muted text-sm py-10 text-center">Select a vintage above.</p>;
  if (isLoading) return <p className="text-text-muted text-sm py-10 text-center animate-pulse">Loading...</p>;

  return (
    <div className="space-y-4">
      {scores.length === 0 ? (
        <p className="text-text-muted text-sm py-10 text-center">No critic scores recorded.</p>
      ) : (
        <div className="border border-border-medium divide-y divide-border-subtle">
          {scores.map((score: CriticScore) => {
            const info = CRITIC_DISPLAY[score.critic] ?? { label: score.critic };
            const isHigh = score.score_type === '100_pt' && score.score_value >= 95;
            const isConfirming = confirmDeleteId === score.id;
            return (
              <div key={score.id} className="group flex items-center justify-between px-4 py-3 bg-surface-1">
                <div>
                  <p className="font-display text-[15px] font-medium text-text-primary">{info.label}</p>
                  {score.critic_individual && <p className="text-xs text-text-muted">{score.critic_individual}</p>}
                </div>
                <div className="flex items-center gap-3">
                  <span className={`font-display text-[28px] font-medium tabular-nums leading-none ${isHigh ? 'text-gold' : 'text-text-primary'}`} dir="ltr">
                    {formatScore(score)}
                  </span>
                  {isOwner && (
                    isConfirming ? (
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          disabled={isDeleting}
                          onClick={() => { deleteScore({ scoreId: score.id, vintageId: vintageId! }, { onSuccess: () => setConfirmDeleteId(null) }); }}
                          className="text-[11px] font-semibold uppercase tracking-[0.08em] text-red-400 hover:text-red-300 transition-colors disabled:opacity-50"
                        >
                          Confirm
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmDeleteId(null)}
                          className="text-[11px] font-semibold uppercase tracking-[0.08em] text-text-muted hover:text-text-primary transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setConfirmDeleteId(score.id)}
                        className="text-text-muted hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
                        title="Delete score"
                        aria-label="Delete score"
                      >
                        ×
                      </button>
                    )
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {isOwner && !showForm && (
        <button type="button" onClick={() => setShowForm(true)}
          className="text-[12px] uppercase tracking-[0.1em] font-semibold text-gold hover:text-gold/80 transition-colors">
          + Add Score
        </button>
      )}

      {isOwner && showForm && vintageId && (
        <AddScorePanel vintageId={vintageId} onClose={() => setShowForm(false)} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: My Bottles
// ---------------------------------------------------------------------------

/** Cellar name + colour, resolved from the single cached cellars query. */
function CellarBadge({ cellarId }: { cellarId: string }) {
  const { data: cellars = [] } = useCellars();
  const cellar = cellars.find((c: Cellar) => c.id === cellarId);
  const color = cellar?.color ?? '#9C9483';
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10.5px] font-semibold uppercase tracking-[0.08em] rounded-[2px] border whitespace-nowrap"
      style={{ color, borderColor: `${color}70`, background: `${color}12` }}>
      {cellar?.icon ? `${cellar.icon} ` : ''}{cellar?.name ?? cellarId}
    </span>
  );
}

/** Shelf / rack / bin name inside a Home cellar. Silent when unset. */
function LocationLabel({ cellarId, locationId }: { cellarId: string; locationId?: string }) {
  const { data: locations = [] } = useCellarLocations(locationId ? cellarId : undefined);
  if (!locationId) return null;
  const loc = locations.find((l: Location) => l.id === locationId);
  return <span className="text-[11px] text-text-tertiary">{loc?.name ?? locationId}</span>;
}

function BottleRow({ bottle, index, isOwner }:
  { bottle: Bottle; index: number; isOwner: boolean }) {
  const showFill = isConcerningFill(bottle.fill_level);
  const showCondition = isConcerningCondition(bottle.label_condition);
  const purchased = formatFullDate(bottle.purchase_date);
  const isOut = bottle.status !== 'in_cellar';

  return (
    <Link to={`/inventory/${bottle.id}`}
      className={`grid grid-cols-[auto_1fr_auto] gap-x-4 gap-y-1.5 items-center px-4 py-3 bg-surface-1 hover:bg-surface-2 transition-colors ${isOut ? 'opacity-60' : ''}`}>

      <span className="font-mono text-[11px] text-text-tertiary tabular-nums w-6">#{index}</span>

      <div className="min-w-0 flex flex-wrap items-center gap-x-2.5 gap-y-1.5">
        <span className="text-sm font-medium text-text-primary whitespace-nowrap">
          {FORMAT_LABELS[bottle.format] ?? bottle.format}
        </span>

        {isOut
          ? <span className="text-[11px] text-text-tertiary">{STATUS_LABELS[bottle.status]}</span>
          : <CellarBadge cellarId={bottle.current_cellar_id} />}
        {!isOut && <LocationLabel cellarId={bottle.current_cellar_id} locationId={bottle.current_location_id} />}

        {bottle.in_OWC && (
          <span className="text-[10.5px] uppercase tracking-[0.08em] font-semibold text-text-tertiary border border-border-medium rounded-[2px] px-1.5 py-0.5">OWC</span>
        )}
        {bottle.is_special_occasion && (
          <span className="text-gold text-[13px] leading-none" title={bottle.special_occasion_reason ?? 'Special occasion'}>★</span>
        )}
        {showFill && (
          <span className="text-[10.5px] uppercase tracking-[0.08em] font-semibold text-orange-300/90 border border-orange-300/40 bg-orange-300/10 rounded-[2px] px-1.5 py-0.5">
            {FILL_LABELS[bottle.fill_level]}
          </span>
        )}
        {showCondition && (
          <span className="text-[10.5px] uppercase tracking-[0.08em] font-semibold text-orange-300/90 border border-orange-300/40 bg-orange-300/10 rounded-[2px] px-1.5 py-0.5">
            Label: {CONDITION_LABELS[bottle.label_condition]}
          </span>
        )}
      </div>

      <div className="text-right whitespace-nowrap flex flex-col items-end gap-0.5">
        {isOwner && (
          <Money value={bottle.cost_eur_at_purchase} currency="EUR"
            className="text-[12.5px] text-text-secondary font-mono" minWidthCh={6} />
        )}
        <span className="text-[11px] text-text-tertiary">
          {SOURCE_LABELS[bottle.purchase_source] ?? bottle.purchase_source}
          {purchased ? ` · ${purchased}` : ''}
        </span>
      </div>
    </Link>
  );
}

function VintageBottleGroup({ vintage, bottles, isActive, isOwner, onSelect }: {
  vintage: Vintage;
  bottles: Bottle[];
  isActive: boolean;
  isOwner: boolean;
  onSelect: () => void;
}) {
  const [open, setOpen] = useState(isActive);
  const inCellar = bottles.filter((b: Bottle) => b.status === 'in_cellar');
  const gone     = bottles.filter((b: Bottle) => b.status !== 'in_cellar');
  const status   = computeDrinkingStatus(vintage);

  const totalCost = inCellar.reduce((s, b: Bottle) => s + (b.cost_eur_at_purchase ?? 0), 0);

  return (
    <div className={`border ${isActive ? 'border-gold/50' : 'border-border-medium'}`}>
      <button type="button"
        onClick={() => { setOpen(o => !o); if (!isActive) onSelect(); }}
        className={`w-full flex items-center justify-between gap-4 px-4 py-3 text-left transition-colors ${
          isActive ? 'bg-surface-2' : 'bg-surface-1 hover:bg-surface-2'
        }`}>
        <div className="flex items-baseline gap-3 min-w-0">
          <span className={`text-[11px] leading-none ${open ? 'text-gold' : 'text-text-tertiary'}`}>{open ? '▾' : '▸'}</span>
          <span className={`font-display text-[20px] font-medium leading-none ${isActive ? 'text-gold' : 'text-text-primary'}`} dir="ltr">
            {vintage.year}
          </span>
          <span className="text-[12px] text-text-secondary truncate">
            {inCellar.length} in cellar
            {gone.length > 0 && ` · ${gone.length} gone`}
          </span>
        </div>
        <div className="flex items-center gap-4 whitespace-nowrap">
          {isOwner && inCellar.length > 0 && (
            <Money value={totalCost} currency="EUR"
              className="text-[12px] text-text-tertiary font-mono" minWidthCh={6} />
          )}
          <span className={`text-[12px] font-medium ${DRINKING_TEXT[status]}`}>{DRINKING_LABEL[status]}</span>
        </div>
      </button>

      {open && (
        <div className="border-t border-border-subtle divide-y divide-border-subtle">
          {inCellar.map((b: Bottle, i: number) => (
            <BottleRow key={b.id} bottle={b} index={i + 1} isOwner={isOwner} />
          ))}
          {gone.length > 0 && (
            <div className="divide-y divide-border-subtle">
              <p className="px-4 py-2 text-[10.5px] uppercase tracking-[0.12em] font-semibold text-text-tertiary bg-surface-0/40">
                No longer in cellar ({gone.length})
              </p>
              {gone.map((b: Bottle, i: number) => (
                <BottleRow key={b.id} bottle={b} index={i + 1} isOwner={isOwner} />
              ))}
            </div>
          )}
          {bottles.length === 0 && (
            <p className="px-4 py-4 text-sm text-text-muted">No bottles recorded for this vintage.</p>
          )}
        </div>
      )}
    </div>
  );
}

function MyBottlesTab({ vintages, activeVintageId, onSelectVintage, isOwner }: {
  vintages: Vintage[];
  activeVintageId: string | undefined;
  onSelectVintage: (v: Vintage) => void;
  isOwner: boolean;
}) {
  const vintageIds = useMemo(() => vintages.map(v => v.id), [vintages]);
  const { data: bottles = [], isLoading } = useBottlesByVintageIds(vintageIds);

  const byVintage = useMemo(() => {
    const map = new Map<string, Bottle[]>();
    for (const b of bottles as Bottle[]) {
      const list = map.get(b.vintage_id);
      if (list) list.push(b); else map.set(b.vintage_id, [b]);
    }
    return map;
  }, [bottles]);

  if (isLoading) return <p className="text-text-muted text-sm py-10 text-center animate-pulse">Loading...</p>;

  // Only vintages that actually hold bottles — an empty accordion row is noise.
  const groups = vintages.filter(v => (byVintage.get(v.id)?.length ?? 0) > 0);

  if (groups.length === 0) return (
    <div className="py-12 text-center space-y-3">
      <p className="text-text-muted text-sm">No bottles of this wine in any cellar.</p>
      <Link to="/inventory/add" className="text-gold text-sm hover:underline">+ Add bottle</Link>
    </div>
  );

  const totalInCellar = (bottles as Bottle[]).filter(b => b.status === 'in_cellar').length;

  return (
    <div className="space-y-3">
      <p className="text-[11px] uppercase tracking-[0.12em] font-semibold text-text-tertiary">
        {totalInCellar} bottle{totalInCellar === 1 ? '' : 's'} across {groups.length} vintage{groups.length === 1 ? '' : 's'}
      </p>
      {groups.map(v => (
        <VintageBottleGroup key={v.id} vintage={v}
          bottles={byVintage.get(v.id) ?? []}
          isActive={v.id === activeVintageId}
          isOwner={isOwner}
          onSelect={() => onSelectVintage(v)} />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Vintages
// ---------------------------------------------------------------------------

function VintageListTab({ vintages, selectedVintage, onSelect }: {
  vintages: Vintage[];
  selectedVintage: Vintage | null;
  onSelect: (v: Vintage) => void;
}) {
  const active = selectedVintage ?? vintages[0] ?? null;
  const status = active ? computeDrinkingStatus(active) : null;

  return (
    <div className="space-y-6">
      {/* Vintage picker */}
      <div className="border border-border-medium divide-y divide-border-subtle">
        {vintages.map(v => {
          const vs = computeDrinkingStatus(v);
          const isSelected = v.id === active?.id;
          return (
            <button key={v.id} type="button" onClick={() => onSelect(v)}
              className={`w-full flex items-center justify-between px-4 py-3 transition-colors text-left ${
                isSelected ? 'bg-surface-2 border-l-2 border-l-gold' : 'bg-surface-1 hover:bg-surface-2'
              }`}>
              <div>
                <p className={`font-display font-bold text-lg ${isSelected ? 'text-gold' : 'text-text-primary'}`} dir="ltr">{v.year}</p>
                <p className="text-xs text-text-muted">
                  Window: <span dir="ltr">{v.drinking_window_current ? `${v.drinking_window_current.from_year}–${v.drinking_window_current.to_year}` : '—'}</span>
                </p>
              </div>
              <div className="text-right">
                <span className={`text-sm font-medium ${DRINKING_TEXT[vs]}`}>{DRINKING_LABEL[vs]}</span>
                {v.vintage_quality_rating && (
                  <p className="text-xs text-text-muted mt-0.5">
                    {'★'.repeat(v.vintage_quality_rating)}{'☆'.repeat(5 - v.vintage_quality_rating)}
                  </p>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Selected vintage detail */}
      {active && status && (
        <div className="space-y-4">
          <div className="bg-surface-1 border border-border-medium px-5 py-4">
            <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-text-tertiary mb-3">
              {active.year} — Drinking Window
            </p>
            {active.drinking_window_current ? (
              <div className="flex items-center gap-4 flex-wrap">
                <div>
                  <p className="text-[11px] text-text-muted mb-0.5">Opens</p>
                  <p className="font-display text-[22px] text-text-primary font-medium" dir="ltr">{active.drinking_window_current.from_year}</p>
                </div>
                <div className="text-text-muted text-xl font-light">→</div>
                <div>
                  <p className="text-[11px] text-text-muted mb-0.5">Peak</p>
                  <p className="font-display text-[22px] text-gold font-medium" dir="ltr">
                    {active.drinking_window_current.peak_from}–{active.drinking_window_current.peak_to}
                  </p>
                </div>
                <div className="text-text-muted text-xl font-light">→</div>
                <div>
                  <p className="text-[11px] text-text-muted mb-0.5">Closes</p>
                  <p className="font-display text-[22px] text-text-primary font-medium" dir="ltr">{active.drinking_window_current.to_year}</p>
                </div>
              </div>
            ) : (
              <p className="text-sm text-text-muted">Analysis pending — run generate-analyses to populate.</p>
            )}
          </div>
          <div className="flex items-center gap-3 bg-surface-1 border border-border-medium px-5 py-4">
            <div className="flex-1">
              <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-text-tertiary mb-1">Status</p>
              <span className={`text-[20px] font-display font-medium ${DRINKING_TEXT[status]}`}>{DRINKING_LABEL[status]}</span>
            </div>
            {active.vintage_quality_rating && (
              <div className="text-right">
                <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-text-tertiary mb-1">Quality</p>
                <p className="text-gold text-lg tracking-wider">
                  {'★'.repeat(active.vintage_quality_rating)}
                  <span className="text-text-muted">{'☆'.repeat(5 - active.vintage_quality_rating)}</span>
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Prices
// ---------------------------------------------------------------------------

function PricesTab({ vintageId, isOwner }: { vintageId: string | undefined; isOwner: boolean }) {
  const { data: history = [], isLoading } = usePriceHistory(vintageId);
  if (!vintageId) return <p className="text-text-muted text-sm py-10 text-center">Select a vintage above.</p>;
  if (isLoading) return <p className="text-text-muted text-sm py-10 text-center animate-pulse">Loading...</p>;
  if (!history.length) return (
    <div className="py-12 text-center space-y-3">
      <p className="text-text-muted text-sm">No market price data.</p>
      {isOwner && <Link to="/prices/refresh" className="text-gold text-sm hover:underline">Run Price Refresh</Link>}
    </div>
  );
  const latest = history[0]!;
  return (
    <div className="space-y-5">
      <div className="bg-surface-1 border border-border-medium p-4">
        <p className="text-xs text-text-muted uppercase tracking-wide mb-1">Current market price</p>
        <p className="font-display text-[32px] font-medium text-gold" dir="ltr">
          <Money value={latest.price} currency="EUR" minWidthCh={7} forceVisible />
        </p>
        <p className="text-xs text-text-muted mt-1">{latest.format} · {formatAsOf(latest.date_observed) ?? '—'}</p>
      </div>
      {history.length > 1 && (
        <div className="border border-border-medium divide-y divide-border-subtle">
          {history.map(entry => (
            <div key={entry.id} className="flex items-center justify-between px-4 py-2.5 bg-surface-1">
              <div>
                <p className="text-sm text-text-primary" dir="ltr">
                  <Money value={entry.price} currency="EUR" minWidthCh={6} forceVisible />
                </p>
                <p className="text-xs text-text-muted">{entry.source.replace(/_/g, ' ')}</p>
              </div>
              <p className="text-xs text-text-muted">{formatAsOf(entry.date_observed) ?? '—'}</p>
            </div>
          ))}
        </div>
      )}
      {isOwner && (
        <div className="flex gap-4">
          <Link to="/prices/refresh" className="text-sm text-gold hover:underline">Refresh prices</Link>
          <Link to="/prices/investment" className="text-sm text-text-secondary hover:text-gold">Portfolio overview</Link>
        </div>
      )}
    </div>
  );
}

type TabId = 'analysis' | 'scores' | 'bottles' | 'vintage' | 'prices';
const TABS: { id: TabId; label: string }[] = [
  { id: 'analysis', label: 'Analysis' },
  { id: 'scores',   label: 'Critic Scores' },
  { id: 'prices',   label: 'Market Price' },
  { id: 'bottles',  label: 'My Bottles' },
  { id: 'vintage',  label: 'Vintages' },
];

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function WineAnalysisPage() {
  const { wineId } = useParams<{ wineId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab]             = useState<TabId>('analysis');
  const [showVintagePicker, setShowVintagePicker] = useState(false);
  const [showEdit, setShowEdit] = useState(false);

  const { data: wine, isLoading: loadingWine } = useWine(wineId);
  const { data: producer }    = useProducer(wine?.producer_id);
  const { data: appellation } = useAppellation(wine?.appellation_id);
  const { data: rawVintages = [] } = useVintagesByWine(wineId);
  const isOwner = useAuthStore(s => s.userDoc?.role === 'owner');
  const queueAnalysis = useQueueWineAnalysis();

  // Newest-first. Firestore returns no guaranteed order, so never rely on [0]
  // of the raw result — that is what silently swapped 2007 for 2009.
  const vintages = useMemo(
    () => [...rawVintages].sort((a, b) => b.year - a.year),
    [rawVintages]
  );

  // The selected vintage lives in the URL (?vintage=2007) so that deep links,
  // the browser Back button and row clicks from Inventory all agree.
  const urlYear = Number(searchParams.get('vintage')) || null;
  const effectiveVintage =
    (urlYear ? vintages.find(v => v.year === urlYear) : undefined)
    ?? vintages[0]
    ?? null;

  // One wine-wide bottle fetch, shared (same query key) with MyBottlesTab.
  const vintageIds = useMemo(() => vintages.map(v => v.id), [vintages]);
  const { data: allBottles = [] } = useBottlesByVintageIds(vintageIds);
  const inCellarAll = useMemo(
    () => allBottles.filter((b: Bottle) => b.status === 'in_cellar'),
    [allBottles]
  );
  const vintageInCellarCount = effectiveVintage
    ? inCellarAll.filter((b: Bottle) => b.vintage_id === effectiveVintage.id).length
    : 0;
  const vintagesWithBottles = new Set(inCellarAll.map((b: Bottle) => b.vintage_id)).size;

  const selectVintage = (v: Vintage) => {
    const next = new URLSearchParams(searchParams);
    next.set('vintage', String(v.year));
    setSearchParams(next, { replace: true });
  };
  const vintageChartRegion = effectiveVintage
    ? inferVintageChartRegion(appellation ?? undefined, wine?.style ?? 'red', wine?.cuvee_name)
    : undefined;
  const classification = appellation
    ? [appellation.classification_level?.replace(/_/g, ' '), appellation.name].filter(Boolean).join(' · ')
    : null;

  if (loadingWine) {
    return (
      <div className="min-h-screen bg-surface-0 flex items-center justify-center">
        <p className="text-text-muted text-sm animate-pulse">Loading...</p>
      </div>
    );
  }
  if (!wine) {
    return (
      <div className="min-h-screen bg-surface-0 flex flex-col items-center justify-center gap-3">
        <p className="text-text-muted text-sm">Wine not found</p>
        <Link to="/wines" className="text-gold text-sm hover:underline">Back to catalog</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-0">
      <div className="max-w-[1280px] mx-auto px-4 md:px-8 relative z-10">

        {/* BACK BAR */}
        <div className="pt-5 flex items-center justify-between">
          <button type="button" onClick={() => navigate(-1)}
            className="text-[12px] uppercase tracking-[0.14em] font-medium text-text-tertiary hover:text-text-primary transition-colors">
            &larr; Inventory{appellation?.region ? ` · ${appellation.region}` : ''}
          </button>
          {isOwner && (
            <div className="flex gap-2">
              <button type="button"
                onClick={() => wineId && queueAnalysis.mutate({ wineId, priority: 'high' })}
                disabled={queueAnalysis.isPending}
                className="bg-surface-2 border border-border-medium text-text-primary px-3.5 py-2 text-[12px] font-medium hover:border-border-strong transition-colors rounded-[4px] disabled:opacity-40">
                {queueAnalysis.isPending ? 'Queuing…' : 'Regenerate Analysis'}
              </button>
              <button type="button"
                onClick={() => setShowEdit(true)}
                className="bg-surface-2 border border-border-medium text-text-primary px-3.5 py-2 text-[12px] font-medium hover:border-border-strong transition-colors rounded-[4px]">
                Edit Manually
              </button>
            </div>
          )}
        </div>

        {/* HERO */}
        <section className="pt-9 pb-8 border-b border-border-subtle">
          {classification && (
            <span className="inline-block text-[11px] uppercase tracking-[0.22em] font-semibold text-gold border border-gold/40 px-3 py-[5px] rounded-[1px] mb-[18px]">
              {classification}
            </span>
          )}
          {producer && (
            <p className="font-display text-[18px] font-normal text-text-secondary uppercase tracking-[0.04em] mb-2">
              {producer.name}
            </p>
          )}
          {effectiveVintage && (
            <p className="font-display text-[32px] xl:text-[40px] font-medium text-gold leading-none tracking-[-0.02em] mb-2" dir="ltr">
              {effectiveVintage.year}
            </p>
          )}
          <h1 className="font-display italic text-[56px] xl:text-[64px] font-light leading-[1.05] tracking-[-0.025em] text-text-primary mb-1">
            {wine.cuvee_name ?? '—'}
          </h1>
          <div className="flex items-center gap-6 mt-5 flex-wrap">
            {vintages.length > 0 && effectiveVintage && (
              <div className="relative">
                <button type="button" onClick={() => setShowVintagePicker(p => !p)}
                  className="flex items-center gap-3 px-4 pl-[18px] py-2.5 bg-surface-2 border border-border-medium rounded-[4px] hover:border-border-strong transition-colors">
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.15em] text-text-tertiary font-semibold leading-none mb-1">Vintage</p>
                    <p className="font-display text-[24px] text-gold font-medium leading-none">{effectiveVintage.year}</p>
                  </div>
                  <span className="text-text-tertiary text-[11px] ml-1">v</span>
                </button>
                {showVintagePicker && (
                  <div className="absolute top-full left-0 mt-1 z-20 bg-surface-2 border border-border-medium shadow-xl min-w-[140px]">
                    {vintages.map(v => (
                      <button key={v.id} type="button"
                        onClick={() => { selectVintage(v); setShowVintagePicker(false); setActiveTab('analysis'); }}
                        className={`w-full text-left px-4 py-2.5 text-sm hover:bg-surface-3 flex items-center justify-between gap-3 ${v.id === effectiveVintage.id ? 'text-gold' : 'text-text-primary'}`}>
                        <span className="font-display font-medium" dir="ltr">{v.year}</span>
                        <span className={`text-[11px] ${DRINKING_TEXT[computeDrinkingStatus(v)]}`}>
                          {DRINKING_LABEL[computeDrinkingStatus(v)]}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="flex flex-col gap-[3px]">
              <span className="text-[11px] uppercase tracking-[0.14em] text-text-tertiary font-semibold">Format</span>
              <span className="font-display text-[18px] text-text-primary font-medium">750 ml</span>
            </div>
            <div className="flex flex-col gap-[3px]">
              <span className="text-[11px] uppercase tracking-[0.14em] text-text-tertiary font-semibold">Style</span>
              <span className="font-display text-[18px] text-text-primary font-medium">{STYLE_LABELS[wine.style]}</span>
            </div>
            {effectiveVintage && effectiveVintage.drinking_window_current && (
              <div className="flex flex-col gap-[3px]">
                <span className="text-[11px] uppercase tracking-[0.14em] text-text-tertiary font-semibold">Drinking from</span>
                <span className="font-display text-[18px] text-text-primary font-medium" dir="ltr">
                  {effectiveVintage.drinking_window_current.from_year}
                </span>
              </div>
            )}
            <div className="flex flex-col gap-[3px]">
              <span className="text-[11px] uppercase tracking-[0.14em] text-text-tertiary font-semibold">My bottles</span>
              <span className="font-display text-[18px] text-text-primary font-medium flex items-baseline gap-2" dir="ltr">
                {vintageInCellarCount}
                {inCellarAll.length > vintageInCellarCount && (
                  <span className="font-sans text-[12px] font-normal text-text-tertiary tracking-normal">
                    of {inCellarAll.length} across {vintagesWithBottles} vintages
                  </span>
                )}
              </span>
            </div>
          </div>
        </section>

        {/* TABS */}
        <div className="flex gap-0 border-b border-border-medium overflow-x-auto">
          {TABS.map(tab => {
            const isActive = activeTab === tab.id;
            const label = tab.id === 'bottles' && inCellarAll.length
              ? `${tab.label} · ${inCellarAll.length}`
              : tab.label;
            return (
              <button key={tab.id} type="button" onClick={() => setActiveTab(tab.id)}
                className={`relative px-[22px] py-4 text-[12px] uppercase tracking-[0.14em] font-semibold whitespace-nowrap transition-colors ${
                  isActive
                    ? 'text-text-primary after:absolute after:bottom-[-1px] after:inset-x-0 after:h-0.5 after:bg-gold'
                    : 'text-text-tertiary hover:text-text-secondary'
                }`}>
                {label}
              </button>
            );
          })}
        </div>

        {/* TWO-COLUMN GRID */}
        <div className="grid grid-cols-1 xl:grid-cols-[1fr_380px] gap-14 pt-12 pb-20">

          {/* MAIN */}
          <article>
            {activeTab === 'analysis' && (
              <AnalysisTab wineId={wineId ?? ''} selectedVintage={effectiveVintage}
                isOwner={isOwner} vintageChartRegion={vintageChartRegion} />
            )}
            {activeTab === 'scores'  && <CriticScoresTab vintageId={effectiveVintage?.id} isOwner={isOwner} />}
            {activeTab === 'bottles' && (
              <MyBottlesTab vintages={vintages} activeVintageId={effectiveVintage?.id}
                onSelectVintage={selectVintage} isOwner={isOwner} />
            )}
            {activeTab === 'vintage' && (
              <VintageListTab vintages={vintages}
                selectedVintage={effectiveVintage}
                onSelect={selectVintage} />
            )}
            {activeTab === 'prices' && <PricesTab vintageId={effectiveVintage?.id} isOwner={isOwner} />}
          </article>

          {/* SIDEBAR */}
          <aside className="xl:sticky xl:top-[90px] xl:self-start flex flex-col gap-5">
            <SidebarCriticScores vintageId={effectiveVintage?.id} />
            <SidebarBottles vintageId={effectiveVintage?.id} totalCount={wine.bottle_count ?? 0} />
            {effectiveVintage && <SidebarDrinkingWindow vintage={effectiveVintage} />}
            <SidebarMarket vintageId={effectiveVintage?.id} />
            <div className="bg-surface-1 border border-claret p-[18px] text-center hover:bg-claret/10 transition-colors flex flex-col items-center gap-2">
              <span className="text-claret text-[22px] leading-none">&#9829;</span>
              <p className="font-display text-[16px] text-text-primary font-medium tracking-[-0.01em]">Add to Wishlist</p>
              <p className="text-[11px] text-text-tertiary italic mb-1">Track for future acquisition</p>
              <WishlistButton
                wineId={wine.id}
                wineName={[producer?.name, wine.cuvee_name].filter(Boolean).join(' — ')}
                defaultYear={effectiveVintage?.year}
              />
            </div>
            <div className="px-[18px] py-3 text-[11px] text-text-muted text-center border border-border-subtle rounded-[1px] leading-[1.7]">
              Generated by <span className="text-gold font-display italic">Claude</span>
              {' · '}
              <span className="font-mono text-text-tertiary">wine-expert v1.2</span>
            </div>
          </aside>

        </div>
      </div>

      {showEdit && isOwner && (
        <EditWineModal
          wine={wine}
          currentAppellationName={appellation?.name}
          onClose={() => setShowEdit(false)}
        />
      )}
    </div>
  );
}
